import clsx from 'clsx'
import styles from './DealHuntSearchBox.module.css'
import { SG_ETHICS_PATH } from 'lib/dealhunt/sponsored'
import type { DealHuntSuggestHit } from 'lib/dealhunt/types'

/** Product rows a section shows at most (the dropdown's height budget). */
const PRODUCT_ROWS = 6

interface SuggestProductSectionsProps {
  hasQuery: boolean
  /** Empty-query heading: "Recently reviewed" (Meili) or "Popular products" (REST fallback). */
  defaultTitle: string
  /** Default state only (`splitDefaultSuggest`); always empty on a typed query. */
  sponsoredRows: DealHuntSuggestHit[]
  /** "Recently reviewed" / "Top results" rows, the sponsored line already removed. */
  reviewedRows: DealHuntSuggestHit[]
  /** The "From our coverage" block for the anchor row — the sponsored row when there is one,
   *  else the first products row. The caller parsed it for that same hit. */
  coverageBlock: React.ReactNode
  /** What an empty products section shows (spinner while loading, else "No matches yet"). */
  emptyNode: React.ReactNode
  /** `index` is 0-based and continuous across both sections (the row's `position` − 1). */
  renderHit: (
    hit: DealHuntSuggestHit,
    index: number,
    appendix: React.ReactNode,
    inSponsoredSection: boolean
  ) => JSX.Element
}

/**
 * The typeahead's Products tab (contract C2): in the default state a "Sponsored" section (with
 * "Why this?" → the ethics statement) above "Recently reviewed" when DealHunt has a live slot;
 * otherwise the products section alone, as before. At most ONE coverage appendix in the
 * dropdown: under the sponsored row when present, else under the first products row.
 */
const SuggestProductSections: React.VFC<SuggestProductSectionsProps> = ({
  hasQuery,
  defaultTitle,
  sponsoredRows,
  reviewedRows,
  coverageBlock,
  emptyNode,
  renderHit,
}) => {
  const sponsored = sponsoredRows.length > 0
  return (
    <>
      {sponsored && (
        <div className={styles.section}>
          <div className={clsx(styles.sectionTitle, styles.sectionTitleRow)}>
            <span>{'Sponsored'}</span>
            {/* The ethics statement is a WP page — a plain full navigation, like post rows. */}
            <a href={SG_ETHICS_PATH} className={styles.whyThis}>
              {'Why this?'}
            </a>
          </div>
          <ul className={styles.hits}>
            {sponsoredRows.map((h, i) => renderHit(h, i, i === 0 ? coverageBlock : null, true))}
          </ul>
        </div>
      )}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{hasQuery ? 'Top results' : defaultTitle}</div>
        {reviewedRows.length > 0 ? (
          <ul className={styles.hits}>
            {reviewedRows.slice(0, PRODUCT_ROWS).map((h, i) =>
              // Positions run on across sections: the sponsored row is 1, the first organic 2.
              renderHit(
                h,
                sponsoredRows.length + i,
                i === 0 && !sponsored ? coverageBlock : null,
                false
              )
            )}
          </ul>
        ) : (
          <div className={styles.noHits}>{emptyNode}</div>
        )}
      </div>
    </>
  )
}

export default SuggestProductSections
